import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { api } from "../api.ts";
import { CLAIM_STATUS, date, dateTime, daysLeft, money, periodName } from "../format.ts";
import { canEdit, useMe } from "../session.ts";
import {
  Badge,
  Button,
  ButtonAnchor,
  Card,
  Dialog,
  Empty,
  ErrorText,
  Field,
  Input,
  LoadError,
  Loading,
  Notice,
  Page,
  Stat,
  Table,
  Tabs,
  Td,
  Textarea,
} from "../ui.tsx";
import { Breakdown, type Explanation } from "./period.tsx";

interface ClaimRow {
  id: string;
  status: string;
  amount_cents: string;
  currency: string;
  deadline: string;
  sent_at: string | null;
  credited_cents: string | null;
  invoice_reference: string | null;
  auto_send_at: string | null;
  vendor_id: string;
  vendor: string;
  period_key: string;
  service: string;
}

export const statusTone = (s: string) =>
  s === "credited"
    ? "ok"
    : s === "expired" || s === "rejected"
      ? "danger"
      : s === "disputed" || s === "partially_credited"
        ? "warn"
        : s === "draft" || s === "ready"
          ? "accent"
          : "neutral";

const GROUPS = {
  open: ["draft", "ready"],
  sent: ["sent", "acknowledged", "disputed"],
  recovered: ["credited", "partially_credited"],
  closed: ["rejected", "expired"],
} as const;
type Group = keyof typeof GROUPS | "all";

export function ClaimsList() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Group) ?? "open";
  const { data, error, isLoading } = useQuery({
    queryKey: ["claims"],
    queryFn: () => api.get<{ claims: ClaimRow[] }>("/claims"),
  });
  const rows = (data?.claims ?? []).filter(
    (c) => tab === "all" || (GROUPS[tab] as readonly string[]).includes(c.status),
  );
  const sum = (k: "amount_cents" | "credited_cents") =>
    rows.reduce((a, c) => a + BigInt(c[k] ?? "0"), 0n).toString();
  const currency = rows[0]?.currency ?? "USD";
  return (
    <Page
      help="claims"
      title="Claims"
      lead="Every month a vendor missed its promise, from draft to the credit on your invoice."
    >
      <Tabs
        label="Claims"
        value={tab}
        onChange={(t) => setParams({ tab: t }, { replace: true })}
        tabs={[
          { id: "open", label: "To send" },
          { id: "sent", label: "Sent" },
          { id: "recovered", label: "Recovered" },
          { id: "closed", label: "Closed" },
          { id: "all", label: "All" },
        ]}
      >
        {tab === "recovered" && rows.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Stat label="Claimed" value={money(sum("amount_cents"), currency)} />
            <Stat
              label="Credited on invoices"
              tone="accent"
              value={money(sum("credited_cents"), currency)}
            />
          </div>
        )}
        <Card flush>
          {isLoading ? (
            <Loading />
          ) : error ? (
            <div className="p-5">
              <ErrorText error={error} />
            </div>
          ) : (
            <Table
              head={[
                "Vendor",
                "Month",
                "Amount",
                tab === "recovered" ? "Credited" : "Deadline",
                "Status",
              ]}
              empty={
                rows.length === 0 && (
                  <Empty title={tab === "open" ? "No claims to send." : "Nothing here yet."}>
                    {tab === "open" &&
                      "ninekeep opens a claim when a closed month owes you a credit."}
                  </Empty>
                )
              }
            >
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-surface-2">
                  <Td>
                    <Link className="font-medium hover:text-accent" to={`/claims/${c.id}`}>
                      {c.vendor}
                    </Link>
                    <div className="text-xs text-muted">{c.service}</div>
                  </Td>
                  <Td>{periodName(c.period_key)}</Td>
                  <Td className="tabular">{money(c.amount_cents, c.currency)}</Td>
                  <Td className="tabular whitespace-nowrap">
                    {tab === "recovered" ? (
                      <>
                        {money(c.credited_cents, c.currency)}
                        {c.invoice_reference && (
                          <div className="text-xs text-muted">Invoice {c.invoice_reference}</div>
                        )}
                      </>
                    ) : (
                      <>
                        {date(c.deadline)}
                        {(c.status === "draft" || c.status === "ready") && (
                          <div className="text-xs text-muted">{daysLeft(c.deadline)} days left</div>
                        )}
                      </>
                    )}
                  </Td>
                  <Td>
                    <Badge tone={statusTone(c.status)}>
                      {c.auto_send_at ? "Sending soon" : CLAIM_STATUS[c.status]}
                    </Badge>
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </Tabs>
    </Page>
  );
}

interface ClaimDetailData {
  /** Why this month waits for a person, and what an admin can do. */
  needsCheck: { reason: string; canRework: boolean; retrying: boolean } | null;
  claim: ClaimRow & {
    letter: { subject: string; body: string } | null;
    violations: { rule: string; detail: string }[];
    recipient: string | null;
    period_id: string;
    case_number: string | null;
    sent_via: string | null;
    held_at: string | null;
    dispute_draft: { subject: string; body: string } | null;
    dispute_violations: { rule: string; detail: string }[];
    form_fields: { kind: string; label: string; value: string }[] | null;
  };
  period: { period_key: string; explanation: Explanation; credit_cents: string | null };
  vendor: {
    name: string;
    account_reference: string | null;
    claims_email: string | null;
    auto_send: boolean;
  };
  service: { name: string; fee: unknown };
  inbound: {
    id: string;
    from_address: string;
    subject: string;
    text: string;
    author: string;
    classification: {
      kind: string;
      creditedAmount: string | null;
      invoiceReference: string | null;
      summary: string;
    } | null;
    confirmed_at: string | null;
    received_at: string;
  }[];
  outbound: {
    id: string;
    to_address: string;
    subject: string;
    sent_at: string;
    provider: string;
  }[];
  pack: { sha256: string; created_at: string } | null;
  route: "email" | "form" | "support_ticket" | "portal";
  form: {
    url: string | null;
    instructions: string | null;
    perSubmission: string | null;
    fields: {
      kind: string;
      label: string;
      hint: string | null;
      required: boolean;
      value: string;
    }[];
    missing: string[];
    ready: boolean;
  } | null;
}

const RULE: Record<string, string> = {
  number_not_in_input: "A figure that isn't in ninekeep's calculation",
  unknown_address: "An address that isn't the vendor's",
  legal_threat: "Wording that sounds like a legal threat",
  tone: "Tone",
  missing: "Missing",
  auto_send: "Not sent automatically",
};

export function ClaimDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const editable = canEdit(me);
  const key = ["claim", id];
  const { data, error, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.get<ClaimDetailData>(`/claims/${id}`),
    refetchInterval: (q) => (q.state.data && !q.state.data.claim.letter ? 2000 : false),
  });
  const [subject, setSubject] = useState("");
  const [bodyText, setBody] = useState("");
  const [dialog, setDialog] = useState<null | "other" | "credit" | "send">(null);
  const [caseNumber, setCaseNumber] = useState("");
  const [credit, setCredit] = useState({ amount: "", invoice: "", partial: false });
  useEffect(() => {
    if (data?.claim.letter) {
      setSubject(data.claim.letter.subject);
      setBody(data.claim.letter.body);
    }
  }, [data?.claim.letter]);

  const act = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["home"] });
      setDialog(null);
    },
  });

  if (isLoading) return <Loading />;
  if (error || !data)
    return (
      <Page help="claims" title="Claim" back={{ to: "/claims", label: "Claims" }}>
        <LoadError what="claim" error={error} back={{ to: "/claims", label: "Claims" }} />
      </Page>
    );
  const c = data.claim;
  const unsent = c.status === "draft" || c.status === "ready";
  const dirty = !!c.letter && (subject !== c.letter.subject || bodyText !== c.letter.body);
  const to = c.recipient ?? data.vendor.claims_email;
  const left = daysLeft(c.deadline);
  const amount = money(c.amount_cents, c.currency);

  return (
    <Page
      help="claims"
      title={`${data.vendor.name}: ${amount}`}
      lead={
        <>
          <Link className="text-accent hover:underline" to={`/vendors/${c.vendor_id}`}>
            {data.service.name}
          </Link>
          ,{" "}
          <Link className="text-accent hover:underline" to={`/months/${c.period_id}`}>
            {periodName(data.period.period_key, true)}
          </Link>
          .{" "}
          {unsent
            ? left >= 0
              ? `Claim by ${date(c.deadline)}.`
              : "The deadline has passed."
            : CLAIM_STATUS[c.status]}
        </>
      }
      back={{ to: "/claims", label: "Claims" }}
      actions={
        <>
          <ButtonAnchor href={`/api/claims/${id}/pack.pdf`}>Proof (PDF)</ButtonAnchor>
          <ButtonAnchor href={`/api/claims/${id}/pack.csv`}>
            Outage times (spreadsheet)
          </ButtonAnchor>
        </>
      }
    >
      <ErrorText error={act.error} />
      {c.auto_send_at && (
        <Notice tone="accent">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>
              ninekeep will send this claim to {to} at {dateTime(c.auto_send_at)} unless someone
              holds it.
            </span>
            <Button onClick={() => act.mutate(() => api.post(`/claims/${id}/hold`))}>Hold</Button>
          </div>
        </Notice>
      )}

      {unsent && data.needsCheck && (
        <NeedsCheck
          claimId={id!}
          check={data.needsCheck}
          currency={c.currency}
          admin={canEdit(me, "admin")}
        />
      )}

      {unsent && data.form && editable && (
        <VendorForm claimId={id!} vendor={data.vendor.name} form={data.form} />
      )}

      {unsent && !data.form && (
        <Card
          title="The letter"
          description={
            to
              ? `To ${to}. ninekeep checks every figure against its calculation before it can be sent.`
              : "Add the vendor's claims email on the vendor's page to send by email."
          }
          actions={
            editable && (
              <>
                <Button
                  variant="ghost"
                  onClick={() => act.mutate(() => api.post(`/claims/${id}/redraft`))}
                >
                  Rewrite the letter
                </Button>
                {dirty && (
                  <Button
                    onClick={() =>
                      act.mutate(() =>
                        api.patch(`/claims/${id}/letter`, { subject, body: bodyText }),
                      )
                    }
                  >
                    Save changes
                  </Button>
                )}
              </>
            )
          }
        >
          {!c.letter ? (
            <p className="text-sm text-muted">ninekeep is drafting the letter…</p>
          ) : (
            <div className="flex flex-col gap-4">
              <Field label="Subject">
                {(fid) => (
                  <Input
                    id={fid}
                    value={subject}
                    readOnly={!editable}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Letter">
                {(fid) => (
                  <Textarea
                    id={fid}
                    rows={18}
                    value={bodyText}
                    readOnly={!editable}
                    onChange={(e) => setBody(e.target.value)}
                    className="font-mono text-code"
                  />
                )}
              </Field>
              {c.violations.length > 0 && (
                <Notice tone="danger">
                  <p className="font-medium">Fix these before sending:</p>
                  <ul className="mt-1 list-disc pl-5">
                    {c.violations.map((v) => (
                      <li key={`${v.rule}:${v.detail}`}>
                        {RULE[v.rule] ?? v.rule}: {v.detail}
                      </li>
                    ))}
                  </ul>
                </Notice>
              )}
              {editable && (
                <div className="flex flex-wrap gap-2">
                  {c.status === "draft" && (
                    <Button
                      disabled={dirty || c.violations.length > 0}
                      onClick={() => act.mutate(() => api.post(`/claims/${id}/ready`))}
                    >
                      Mark ready
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    disabled={dirty || !to || c.violations.length > 0 || left < 0}
                    onClick={() => setDialog("send")}
                  >
                    Send by email
                  </Button>
                  <Button variant="ghost" disabled={left < 0} onClick={() => setDialog("other")}>
                    I sent it another way
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted">
                ninekeep is not a law firm. This letter asks for the credit your agreement provides;
                it doesn't threaten legal action.
              </p>
            </div>
          )}
        </Card>
      )}

      {!unsent && (
        <Card
          title="Where it stands"
          description={
            c.sent_at
              ? `Sent ${dateTime(c.sent_at)}${c.sent_via === "other" ? " another way" : ` to ${to}`}${c.case_number ? `, case ${c.case_number}` : ""}.`
              : undefined
          }
          actions={
            editable &&
            ["sent", "acknowledged", "disputed", "partially_credited", "rejected"].includes(
              c.status,
            ) && (
              <>
                {c.status === "sent" && (
                  <Button
                    onClick={() =>
                      act.mutate(() => api.post(`/claims/${id}/status`, { status: "acknowledged" }))
                    }
                  >
                    Vendor acknowledged
                  </Button>
                )}
                {c.status !== "disputed" && (
                  <Button
                    onClick={() =>
                      act.mutate(() => api.post(`/claims/${id}/status`, { status: "disputed" }))
                    }
                  >
                    Vendor disputed
                  </Button>
                )}
                <Button variant="primary" onClick={() => setDialog("credit")}>
                  Record the credit
                </Button>
              </>
            )
          }
        >
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Badge tone={statusTone(c.status)}>{CLAIM_STATUS[c.status]}</Badge>
            {c.credited_cents && (
              <span>
                {money(c.credited_cents, c.currency)} credited
                {c.invoice_reference ? ` on invoice ${c.invoice_reference}` : ""}
              </span>
            )}
          </div>
        </Card>
      )}

      {(c.status === "disputed" || c.status === "rejected") && editable && (
        <DisputeReply claimId={id!} draft={c.dispute_draft} violations={c.dispute_violations} />
      )}

      {data.inbound.length > 0 && (
        <Card
          title="Replies"
          description="Forwarded or CC'd mail from the vendor about this claim."
          flush
        >
          <ul className="divide-y divide-line">
            {data.inbound.map((m) => (
              <li key={m.id} className="flex flex-col gap-2 px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="font-medium">{m.from_address}</span>
                  <span className="text-muted">{dateTime(m.received_at)}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                {m.classification && !m.confirmed_at && editable && (
                  <Notice tone="accent">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span>
                        This looks like{" "}
                        {m.classification.kind === "credit_confirmation"
                          ? `a credit${m.classification.creditedAmount ? ` of ${m.classification.creditedAmount}` : ""}`
                          : m.classification.kind === "dispute"
                            ? "a dispute"
                            : m.classification.kind === "acknowledgment"
                              ? "an acknowledgment"
                              : "something else"}
                        . Is that right?
                      </span>
                      <div className="flex gap-2">
                        {m.classification.kind === "credit_confirmation" ? (
                          <Button
                            variant="primary"
                            onClick={() => {
                              setCredit({
                                amount: m.classification?.creditedAmount ?? "",
                                invoice: m.classification?.invoiceReference ?? "",
                                partial: false,
                              });
                              setDialog("credit");
                            }}
                          >
                            Record the credit
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            onClick={() =>
                              act.mutate(() =>
                                api.post(`/messages/${m.id}/confirm`, {
                                  kind: m.classification!.kind,
                                }),
                              )
                            }
                          >
                            Yes
                          </Button>
                        )}
                        <Button
                          onClick={() =>
                            act.mutate(() =>
                              api.post(`/messages/${m.id}/confirm`, { kind: "other" }),
                            )
                          }
                        >
                          No
                        </Button>
                      </div>
                    </div>
                  </Notice>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Breakdown
        e={data.period.explanation}
        met={false}
        provisional={false}
        creditCents={c.amount_cents}
        currency={c.currency}
      />

      {data.pack && (
        <p className="text-xs break-all text-muted">Proof fingerprint {data.pack.sha256}</p>
      )}

      <Dialog
        open={dialog === "send"}
        title="Send this claim"
        onClose={() => setDialog(null)}
        footer={
          <>
            <Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button
              variant="primary"
              busy={act.isPending}
              onClick={() => act.mutate(() => api.post(`/claims/${id}/send`))}
            >
              Send to {to}
            </Button>
          </>
        }
      >
        <p className="text-sm">
          ninekeep emails the letter with the evidence pack attached, and files the vendor's replies
          to this claim.
        </p>
      </Dialog>

      <Dialog
        open={dialog === "other"}
        title="Sent another way"
        onClose={() => setDialog(null)}
        footer={
          <>
            <Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button
              variant="primary"
              busy={act.isPending}
              onClick={() =>
                act.mutate(() =>
                  api.post(`/claims/${id}/mark-sent`, { caseNumber: caseNumber || null }),
                )
              }
            >
              Record as sent
            </Button>
          </>
        }
      >
        <p className="text-sm">
          For a vendor's web form, support ticket or portal. ninekeep threads the vendor's replies
          by the case number.
        </p>
        <Field
          label="Vendor's case or ticket number"
          hint="Optional; add it later if you don't have it yet."
        >
          {(fid) => (
            <Input id={fid} value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} />
          )}
        </Field>
      </Dialog>

      <Dialog
        open={dialog === "credit"}
        title="Record the credit"
        onClose={() => setDialog(null)}
        footer={
          <>
            <Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button
              variant="primary"
              busy={act.isPending}
              disabled={!/^\d+(\.\d{1,2})?$/.test(credit.amount.replace(/,/g, ""))}
              onClick={() => {
                const [w, f = ""] = credit.amount.replace(/,/g, "").split(".");
                const cents = `${BigInt(w!) * 100n + BigInt(f.padEnd(2, "0").slice(0, 2) || "0")}`;
                const msg = data.inbound.find((m) => m.classification && !m.confirmed_at);
                act.mutate(() =>
                  msg
                    ? api.post(`/messages/${msg.id}/confirm`, {
                        kind: "credit_confirmation",
                        creditedCents: cents,
                        invoiceReference: credit.invoice || null,
                        partial: credit.partial,
                      })
                    : api.post(`/claims/${id}/status`, {
                        status: credit.partial ? "partially_credited" : "credited",
                        creditedCents: cents,
                        invoiceReference: credit.invoice || null,
                      }),
                );
              }}
            >
              Record
            </Button>
          </>
        }
      >
        <Field label={`Amount credited (${c.currency})`} hint={`The claim was for ${amount}.`}>
          {(fid) => (
            <Input
              id={fid}
              inputMode="decimal"
              value={credit.amount}
              onChange={(e) => setCredit({ ...credit, amount: e.target.value })}
            />
          )}
        </Field>
        <Field label="Invoice reference">
          {(fid) => (
            <Input
              id={fid}
              value={credit.invoice}
              onChange={(e) => setCredit({ ...credit, invoice: e.target.value })}
            />
          )}
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={credit.partial}
            onChange={(e) => setCredit({ ...credit, partial: e.target.checked })}
          />{" "}
          Only part of the claim was credited
        </label>
      </Dialog>
    </Page>
  );
}

function DisputeReply({
  claimId,
  draft,
  violations,
}: {
  claimId: string;
  draft: { subject: string; body: string } | null;
  violations: { rule: string; detail: string }[];
}) {
  const qc = useQueryClient();
  const [subject, setSubject] = useState(draft?.subject ?? "");
  const [text, setText] = useState(draft?.body ?? "");
  useEffect(() => {
    setSubject(draft?.subject ?? "");
    setText(draft?.body ?? "");
  }, [draft]);
  const m = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claim", claimId] }),
  });
  const dirty = !!draft && (subject !== draft.subject || text !== draft.body);
  return (
    <Card
      title="Reply to the dispute"
      description="ninekeep drafts a reply from its own figures and the contract's words. You review it and send it; replies are never sent automatically."
      actions={
        <Button
          busy={m.isPending && !draft}
          onClick={() => m.mutate(() => api.post(`/claims/${claimId}/dispute-reply`))}
        >
          {draft ? "Draft again" : "Draft a reply"}
        </Button>
      }
    >
      <ErrorText error={m.error} />
      {draft && (
        <div className="flex flex-col gap-4">
          <Field label="Subject">
            {(fid) => (
              <Input id={fid} value={subject} onChange={(e) => setSubject(e.target.value)} />
            )}
          </Field>
          <Field label="Reply">
            {(fid) => (
              <Textarea
                id={fid}
                rows={14}
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="font-mono text-code"
              />
            )}
          </Field>
          {violations.length > 0 && (
            <Notice tone="danger">
              <p className="font-medium">Fix these before sending:</p>
              <ul className="mt-1 list-disc pl-5">
                {violations.map((v) => (
                  <li key={`${v.rule}:${v.detail}`}>
                    {RULE[v.rule] ?? v.rule}: {v.detail}
                  </li>
                ))}
              </ul>
            </Notice>
          )}
          <div className="flex gap-2">
            {dirty && (
              <Button
                onClick={() =>
                  m.mutate(() =>
                    api.patch(`/claims/${claimId}/dispute-reply`, { subject, body: text }),
                  )
                }
              >
                Save changes
              </Button>
            )}
            <Button
              variant="primary"
              disabled={dirty || violations.length > 0}
              busy={m.isPending}
              onClick={() => m.mutate(() => api.post(`/claims/${claimId}/dispute-reply/send`))}
            >
              Send reply
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function VendorForm({
  claimId,
  vendor,
  form,
}: {
  claimId: string;
  vendor: string;
  form: NonNullable<ClaimDetailData["form"]>;
}) {
  const qc = useQueryClient();
  const [caseNumber, setCaseNumber] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
    } catch {
      setCopied(null);
    }
  };
  const m = useMutation({
    mutationFn: () =>
      api.post(`/claims/${claimId}/form-submitted`, { caseNumber: caseNumber || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claim", claimId] });
      qc.invalidateQueries({ queryKey: ["home"] });
    },
  });
  const all = form.fields.map((f) => `${f.label}:\n${f.value}`).join("\n\n");
  return (
    <Card
      title={`Fill in ${vendor}'s form`}
      description={
        form.instructions ??
        `${vendor} takes claims on its own site. Every field is prefilled where ninekeep can; ninekeep never submits a vendor's form.`
      }
      actions={
        <>
          <Button onClick={() => copy("all", all)}>
            {copied === "all" ? "Copied" : "Copy all"}
          </Button>
          {form.url && (
            <ButtonAnchor
              variant="primary"
              href={form.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              Open {vendor}'s form
            </ButtonAnchor>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {form.perSubmission && <p className="text-sm text-muted">{form.perSubmission}</p>}
        {form.missing.length > 0 && (
          <Notice>
            <ul className="list-disc pl-5">
              {form.missing.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </Notice>
        )}
        {form.fields.map((f) => (
          <div key={f.kind} className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">
                {f.label}
                {f.required ? "" : " (optional)"}
              </span>
              <Button variant="ghost" disabled={!f.value} onClick={() => copy(f.kind, f.value)}>
                {copied === f.kind ? "Copied" : "Copy"}
              </Button>
            </div>
            {f.hint && <p className="text-xs text-muted">{f.hint}</p>}
            <pre className="rounded-chip bg-surface-2 px-3 py-2 text-code whitespace-pre-wrap">
              {f.value || "—"}
            </pre>
          </div>
        ))}
        <div className="flex flex-col gap-2 border-t border-line pt-4 sm:flex-row sm:items-end">
          <Field
            label={`${vendor}'s case number`}
            hint="Optional; add it later if you don't have it yet."
          >
            {(fid) => (
              <Input id={fid} value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} />
            )}
          </Field>
          <Button
            variant="primary"
            disabled={!form.ready}
            busy={m.isPending}
            onClick={() => m.mutate()}
          >
            I submitted it
          </Button>
        </div>
        <ErrorText error={m.error} />
      </div>
    </Card>
  );
}

/**
 * A month ninekeep can't measure again on its own. The claim waits; an admin either has it
 * re-worked with the terms that applied that month, or checks it by hand and enters the credit.
 */
function NeedsCheck({
  claimId,
  check,
  currency,
  admin,
}: {
  claimId: string;
  check: { reason: string; canRework: boolean; retrying: boolean };
  currency: string;
  admin: boolean;
}) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const done = () => {
    qc.invalidateQueries({ queryKey: ["claim", claimId] });
    qc.invalidateQueries({ queryKey: ["claims"] });
  };
  const rework = useMutation({
    mutationFn: () => api.post(`/claims/${claimId}/rework-archived`),
    onSuccess: done,
  });
  const confirm = useMutation({
    mutationFn: () =>
      api.post(`/claims/${claimId}/confirm-figures`, {
        amountCents: String(Math.round(Number(amount.replace(/,/g, "")) * 100)),
        reason,
      }),
    onSuccess: done,
  });
  const amountOk =
    /^\d[\d,]*(?:\.\d{1,2})?$/.test(amount.trim()) && Number(amount.replace(/,/g, "")) > 0;
  return (
    <Card
      title="This claim needs a person"
      description={`${check.reason} It won't be sent until then.`}
    >
      {check.retrying || !admin ? (
        <p className="text-sm text-muted">
          {check.retrying
            ? "Nothing to do yet."
            : "An admin in your organization can re-work it or confirm its figures."}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {check.canRework && (
            <div className="flex flex-col gap-2">
              <p className="text-sm">
                ninekeep can measure the month again with the terms that applied to it and the fee
                you pay now.
              </p>
              <div>
                <Button busy={rework.isPending} onClick={() => rework.mutate()}>
                  Re-work with those terms
                </Button>
              </div>
              <ErrorText error={rework.error} />
            </div>
          )}
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (amountOk && reason.trim().length >= 3) confirm.mutate();
            }}
          >
            <p className="text-sm">
              Or check the month by hand and enter the credit you worked out. It replaces ninekeep's
              figure, and the letter is drafted again.
            </p>
            <Field label={`Credit (${currency})`}>
              {(fid) => (
                <input
                  id={fid}
                  inputMode="decimal"
                  className="h-9 w-48 rounded-control border border-line bg-surface px-3 text-sm"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              )}
            </Field>
            <Field label="How you checked it">
              {(fid) => (
                <input
                  id={fid}
                  className="h-9 w-full rounded-control border border-line bg-surface px-3 text-sm"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="For example: worked out from the March invoice and the old terms"
                />
              )}
            </Field>
            <div>
              <Button
                type="submit"
                busy={confirm.isPending}
                disabled={!amountOk || reason.trim().length < 3}
              >
                Confirm figures
              </Button>
            </div>
            <ErrorText error={confirm.error} />
          </form>
        </div>
      )}
    </Card>
  );
}
